import { useEffect, useState } from "react";
import { Download, FileText, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const SHEET_TYPE_LABELS = {
  worksheet: "Worksheet",
  research_sheet: "Research Sheet",
  graphic_organizer: "Graphic Organizer",
  reading_handout: "Reading Handout",
  reference_card: "Reference Card",
  exit_ticket: "Exit Ticket",
  checklist: "Checklist",
};

const _refinedSeenKey = (activityId) =>
  `atomicedu.et_refined_seen.${activityId || "unknown"}`;

/**
 * "Exit ticket refined ✨" chip.
 *
 * Feb 2026 — teacher-visibility signal on top of the lazy exit-ticket
 * upgrader (backend/exit_ticket_upgrader.py). When a sheet's
 * `_exit_ticket_upgraded_at` breadcrumb is present AND the teacher
 * has not yet seen the chip for this activity, we surface a tiny
 * mint chip on the sheet row. It auto-fades after 4s AND persists a
 * "seen" flag in localStorage so it never re-appears for the same
 * lesson.
 */
const ExitTicketRefinedChip = ({ activityId }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let seen = false;
    try {
      seen = !!window.localStorage.getItem(_refinedSeenKey(activityId));
    } catch (_e) {
      seen = false;
    }
    if (seen) return;
    setVisible(true);
    const persist = () => {
      try {
        window.localStorage.setItem(_refinedSeenKey(activityId), "1");
      } catch (_e) {
        /* private-mode / storage-disabled — silent */
      }
    };
    const hide = window.setTimeout(() => {
      setVisible(false);
      persist();
    }, 4000);
    return () => window.clearTimeout(hide);
  }, [activityId]);

  if (!visible) return null;
  return (
    <span
      data-testid="exit-ticket-refined-chip"
      className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-[#96ded1]/25 text-[9px] font-bold uppercase tracking-[0.16em] text-[#1c2d3d] transition-opacity duration-500"
      title="This exit ticket was refined with lesson-specific prompts."
    >
      <Sparkles className="w-2.5 h-2.5" />
      Exit ticket refined
    </span>
  );
};

const fileSafe = (name) =>
  (name || "Lesson").replace(/[\\/:*?"<>|]+/g, "").trim() || "Lesson";

/**
 * Phase 101 — Student Sheet PDF Bundle UI.
 *
 * Renders the "Student Materials" section at the bottom of a lesson or
 * unit document. Each sheet is listed with its label, and a single
 * mint Download button fetches the combined PDF.
 *
 * Props:
 * - activity: the saved Activity doc. We read `student_sheets` off it.
 * - lessonIndex: 1-based index of the lesson within a unit. Pass null
 *   for a single-lesson activity.
 * - lessonTitle: optional override for the section heading.
 */
export const StudentMaterialsSection = ({ activity, lessonIndex = null, lessonTitle = null }) => {
  const [busy, setBusy] = useState(false);
  if (!activity?.id) return null;

  // Extract the flat sheet list. For units, dig into the matching
  // lesson row. For lessons, the array is already flat.
  let sheets = [];
  const raw = activity.student_sheets;
  if (!Array.isArray(raw) || raw.length === 0) return null;
  if (lessonIndex !== null && lessonIndex !== undefined) {
    const row = raw.find((r) => Number(r?.lesson_index) === Number(lessonIndex));
    sheets = Array.isArray(row?.sheets) ? row.sheets : [];
  } else if (raw[0] && typeof raw[0] === "object" && "sheets" in raw[0]) {
    // Defensive — a unit-shaped array on a single-lesson view: don't render.
    return null;
  } else {
    sheets = raw.filter((s) => s && typeof s === "object");
  }
  if (sheets.length === 0) return null;

  const heading = lessonTitle
    ? `Student Materials — ${lessonTitle}`
    : `Student Materials — ${activity.activity_name || "Lesson"}`;

  const handleDownload = async () => {
    setBusy(true);
    try {
      const api = process.env.REACT_APP_BACKEND_URL;
      const params = new URLSearchParams({ format: "student_sheets" });
      if (lessonIndex !== null && lessonIndex !== undefined) {
        params.set("lesson_index", String(lessonIndex));
      }
      const res = await fetch(
        `${api}/api/activities/${activity.id}/export?${params.toString()}`,
        { credentials: "include" }
      );
      if (!res.ok) {
        const detail = (await res.json().catch(() => null))?.detail || `HTTP ${res.status}`;
        throw new Error(detail);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        lessonIndex !== null && lessonIndex !== undefined
          ? `${fileSafe(activity.activity_name)} - Lesson ${lessonIndex} - Student Sheets.pdf`
          : `${fileSafe(activity.activity_name)} - Student Sheets.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Student sheets downloaded.");
    } catch (e) {
      toast.error(`Couldn't download: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid={
        lessonIndex !== null && lessonIndex !== undefined
          ? `student-materials-lesson-${lessonIndex}`
          : "student-materials"
      }
      className="mt-12 pt-10 border-t-2 border-dashed border-[#96ded1]/60"
    >
      <div className="mb-5 flex items-center gap-2">
        <span
          className="inline-block px-3 py-1 rounded-full bg-[#96ded1]/30 text-[#1c2d3d] text-[10px] font-extrabold uppercase tracking-[0.2em]"
          data-testid="student-materials-eyebrow"
        >
          Student Materials
        </span>
        <span className="text-[#4a6984]/70 text-xs font-medium">
          {sheets.length} sheet{sheets.length === 1 ? "" : "s"} • Print-ready PDF
        </span>
      </div>
      <h3
        className="font-extrabold text-2xl text-[#4a6984] tracking-tight mb-4"
        data-testid="student-materials-heading"
      >
        {heading}
      </h3>
      <ul
        className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-2"
        data-testid="student-materials-list"
      >
        {sheets.map((s, idx) => (
          <li
            key={idx}
            data-testid={`student-sheet-row-${idx}`}
            className="flex items-start gap-3 px-4 py-3 rounded-lg bg-[#f4f5f7] border border-[#4a6984]/10"
          >
            <FileText className="w-4 h-4 mt-0.5 text-[#96ded1] shrink-0" />
            <div className="min-w-0">
              <p className="font-bold text-sm text-[#1c2d3d] truncate">
                {s.title || "Student Sheet"}
              </p>
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/70 mt-0.5">
                {SHEET_TYPE_LABELS[s.sheet_type] || s.sheet_type || "Sheet"}
              </p>
              {s.sheet_type === "exit_ticket" && s._exit_ticket_upgraded_at && (
                <ExitTicketRefinedChip activityId={activity.id} />
              )}
            </div>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={handleDownload}
        disabled={busy}
        data-testid={
          lessonIndex !== null && lessonIndex !== undefined
            ? `download-student-sheets-${lessonIndex}`
            : "download-student-sheets"
        }
        className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#96ded1] text-[#1c2d3d] font-bold text-sm hover:bg-[#7fc9bb] transition disabled:opacity-60 disabled:cursor-wait"
      >
        {busy ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Download className="w-4 h-4" />
        )}
        {busy ? "Building PDF…" : "Download Student Sheets PDF"}
      </button>
    </section>
  );
};

export default StudentMaterialsSection;
